/** X-axis label helpers for multi-time graphs, whose axis is *elapsed seconds*
 *  (each plot rebased to its own start) rather than wall-clock time. The label
 *  format is chosen from the longest plot on the graph:
 *    < 1 hour  -> mm:ss
 *    < 1 day   -> hh:mm:ss
 *    >= 1 day  -> Nd hh:mm   (elapsed days + time)
 */

const HOUR_SEC = 3600;
const DAY_SEC = 86400;

/** Largest individual plot duration (seconds) across the given series. Each
 *  plot's span is its own max-min timestamp (so negative pre-event samples in
 *  RELATIVE/HISTORICAL plots are counted). Empty series are skipped. */
export function maxPlotDurationSec(series: { timestamps: number[] }[]): number {
  let max = 0;
  for (const s of series) {
    if (s.timestamps.length === 0) continue;
    let lo = s.timestamps[0];
    let hi = s.timestamps[0];
    for (const t of s.timestamps) {
      if (t < lo) lo = t;
      if (t > hi) hi = t;
    }
    const span = hi - lo;
    if (span > max) max = span;
  }
  return max;
}

const pad2 = (n: number) => n.toString().padStart(2, "0");

/** Format one elapsed-seconds tick value for a multi-time X axis. `maxDurationSec`
 *  is the longest plot duration on the graph (see `maxPlotDurationSec`) and picks
 *  the format tier. Negative offsets (pre-event samples) keep a leading "-". */
export function formatMultiTimeAxisLabel(seconds: number, maxDurationSec: number): string {
  const sign = seconds < 0 ? "-" : "";
  const total = Math.floor(Math.abs(seconds));

  if (maxDurationSec < HOUR_SEC) {
    return `${sign}${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
  }
  if (maxDurationSec < DAY_SEC) {
    return `${sign}${pad2(Math.floor(total / HOUR_SEC))}:${pad2(Math.floor((total % HOUR_SEC) / 60))}:${pad2(total % 60)}`;
  }
  const days = Math.floor(total / DAY_SEC);
  return `${sign}${days}d ${pad2(Math.floor((total % DAY_SEC) / HOUR_SEC))}:${pad2(Math.floor((total % HOUR_SEC) / 60))}`;
}
